// Small pieces shared by the dashboard designer's canvas and panels.

import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import type { DashboardVisual } from './types'

export type PanelTab = 'layout' | 'criteria' | 'drill' | 'settings'

// VISUAL_CONFIG_REF stores the on-disk filename (`model_<obj>.json`). The
// visual loader takes obj_name, so strip the wrapper.
export function visualRefToObjName(ref: string): string {
  if (!ref) return ''
  const stem = ref.endsWith('.json') ? ref.slice(0, -5) : ref
  return stem.startsWith('model_') ? stem.slice(6) : stem
}

// The visualContents cache key for a tile's underlying model: the visual's
// VISUAL_CONFIG_REF filename, or `report:<obj>` for a report tile (report obj
// names can't collide with `model_*.json` filenames, so one cache serves both).
export function tileContentKey(tile: DashboardVisual): string {
  if (tile.TILE_KIND === 'report') return tile.REPORT_OBJ ? `report:${tile.REPORT_OBJ}` : ''
  return tile.VISUAL_CONFIG_REF || ''
}

/** A row-wide on/off switch with a title and a line of explanation. */
export function Switch({ on, title, detail, onChange, disabled }: {
  on: boolean
  title: string
  detail?: string
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className="dd-sw"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      <span className="t"><b>{title}</b>{detail && <span>{detail}</span>}</span>
      <span className="dd-trk" aria-hidden="true" />
    </button>
  )
}

/** A panel section with an uppercase heading and an optional right-hand note. */
export function Sec({ title, right, children, first }: {
  title?: ReactNode
  right?: ReactNode
  children: ReactNode
  first?: boolean
}) {
  return (
    <div className="dd-sec" style={first ? { borderTop: 'none' } : undefined}>
      {title && <div className="dd-sec-h">{title}{right != null && <span className="r">{right}</span>}</div>}
      {children}
    </div>
  )
}

export function Chevron({ open }: { open: boolean }) {
  return <ChevronDown size={18} className={`dd-chev${open ? ' up' : ''}`} aria-hidden="true" />
}
